import { useState } from "react"
import { Grid, Typography, TextField, Button } from "@mui/material"
import { ISettingsItem } from "../../../Settings"
import { useForm } from "../../../Form"
import { useTranslation } from "../../../i18n"
import requests from "../../../api/client"

export interface VoucherParams {
  retorno: boolean
  message: string
  nome: string
}

export default function VoucherValidation({ name, variables }: ISettingsItem) {
  const [loadingVoucher, setLoadingVoucher] = useState(false)
  const [voucherError, setVoucherError] = useState<boolean>(false)
  const { currentFields: { voucherClub, isVoucherValid }, setCurrentFields, updateFormState } = useForm()
  const { t } = useTranslation()
  const { text } = variables || {}
  const defaultTextArray = [
    t.page1.voucher.iHaveCode,    // índice 0: primeira parte do título
    t.page1.voucher.contelClub,   // índice 1: segunda parte do título (bold)
    t.page1.voucher.digitCode,    // índice 2: subtítulo
    t.page1.voucher.placeholder,  // índice 3: placeholder do input
    t.page1.voucher.validate,     // índice 4: texto do botão
  ]
  
  const mergedArray = text
    ? text.map((value, index) => (value !== undefined && value !== '' ? value : defaultTextArray[index]))
    : defaultTextArray
  
  const handleValidate = async () => {
    setLoadingVoucher(true)
    setVoucherError(false)
    try {
      const { retorno, nome } = await requests.voucherValidator(voucherClub)

      if (retorno) {
        const firstName = nome.split(' ')[0]
        const voucherName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
        updateFormState({ isVoucherValid: true, voucherName })
      } else {
        setVoucherError(true)
      }
      setLoadingVoucher(false)
    } catch (error) {
      setLoadingVoucher(false)
      setVoucherError(true)
      console.error('Voucher inválido:', error)
    }
  }

  return (
    <Grid container item className={name ? `${name} voucher-validation` : "voucher-validation"} xs={12}
      sx={{
        pb: { xs: '20px', lg: '15px', xl: '20px' },
        gap: { xs: '5px', md: '5px' },
      }}
    >
      {/* Título em duas partes */}
      <Grid container item className="titleArea" xs={12}
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '18px', md: '11px', lg: '13px', xl: '18px' },
            fontWeight: 400,
            lineHeight: 1.2
          }}
        >
          {mergedArray[0]}
        </Typography>
        <Typography
          variant="h3"
          className="span"
          sx={{
            fontSize: { xs: '18px', md: '11px', lg: '13px', xl: '18px' },
            fontWeight: 700,
            lineHeight: 1.2
          }}
        >
          {mergedArray[1]}
        </Typography>
      </Grid>

      {/* Subtítulo */}
      <Typography
        variant="body2"
        sx={{
          fontSize: { xs: '14px', md: '9px', lg: '11px', xl: '16px' },
          fontWeight: 400,
          mb: { xs: '12px', md: '5px', xl: '10px' }
        }}
      >
        {mergedArray[2]}
      </Typography>

      {/* Input + Botão */}
      <Grid container item className="inputArea" xs={12}>
        <TextField
          placeholder={mergedArray[3]}
          variant="outlined"
          name="voucherClub"
          value={voucherClub}
          onChange={setCurrentFields}
          error={voucherError}
          helperText={voucherError ? 'Digite um voucher válido!' : isVoucherValid ? 'Cupom válido!' : ''}
          fullWidth
          InputProps={{
            endAdornment: (
              <Button
                onClick={handleValidate}
                disabled={loadingVoucher || voucherClub === ''}
                className="button"
                sx={{
                  borderRadius: '20px',
                  px: { xs: '16px', md: '10px', xl: '16px' },
                  py: { xs: '6px', md: '2px', xl: '6px' },
                  fontSize: { xs: '10px', xl: '12px' },
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.5px'
                }}
              >
                {mergedArray[4]}
              </Button>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              backgroundColor: 'transparent',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.6)',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.8)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#FFFFFF',
              },
            },
            '& .MuiOutlinedInput-input': {
              color: 'rgba(255, 255, 255, 0.45)',
              fontSize: { xs: '16px', md: '12px', lg: '14px', xl: '16px' },
              py: { xs: '18px', md: '8px', lg: '10px', xl: '18px' },
              px: { xs: '14px', md: '10px', lg: '14px', xl: '14px' },
              '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.45)',
                opacity: 1,
              },
            },
          }}
        />
      </Grid>
    </Grid>
  )
}